import React, { useState, useRef } from 'react';
import { Mic, Square, Send, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const VoiceRecorder = ({ onSend, onCancel }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const { toast } = useToast();

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      
      // Start timer
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast({
        variant: 'destructive',
        title: 'Microphone Error',
        description: 'Could not access your microphone. Please check permissions.',
      });
    }
  };

  const stopRecording = async (shouldSend = false) => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);

      if (shouldSend) {
        // Wait for the blob to be created
        mediaRecorderRef.current.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const stream = mediaRecorderRef.current.stream;
          stream.getTracks().forEach(track => track.stop());
          
          if (onSend) {
            await onSend(audioBlob);
          }
        };
      } else {
        if (onCancel) {
          onCancel();
        }
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isRecording) {
    return (
      <button
        onClick={startRecording}
        className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
        title="Record voice message"
      >
        <Mic className="w-5 h-5 text-white" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-3 bg-red-500 bg-opacity-20 rounded-lg px-4 py-2">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
        <span className="text-sm text-white font-mono">{formatTime(recordingTime)}</span>
      </div>
      
      <div className="flex gap-2">
        <button
          onClick={() => stopRecording(false)}
          className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
          title="Cancel"
        >
          <X className="w-4 h-4 text-white" />
        </button>
        <button
          onClick={() => stopRecording(true)}
          className="p-2 rounded-full bg-blue-500 hover:bg-blue-600 transition-colors"
          title="Send"
        >
          <Send className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
};

export default VoiceRecorder;
